/**
 * motion-estimator.js — global inter-frame motion via FFT phase correlation.
 *
 * Part of the /remove-text-from-video reconstruction engine. To reconstruct the
 * background behind a screen-FIXED overlay (subtitle / logo / watermark), we must
 * pull the true background from OTHER frames where the camera/scene moved it out
 * from under the overlay. That requires knowing how the background moved between
 * frames — this module estimates that motion.
 *
 * v1 estimates a global TRANSLATION (dx, dy) per frame pair via normalized
 * cross-power-spectrum phase correlation:
 *
 *     R(f) = A(f)·conj(B(f)) / |A(f)·conj(B(f)|        (unit-magnitude)
 *     corr = IFFT(R)                                    (real surface)
 *     peak of corr → integer shift; parabolic interp → sub-pixel
 *
 * Phase correlation is chosen as the primary estimator because it is:
 *   • deterministic, sub-pixel, and robust on low-texture / compressed footage
 *     (the case where sparse feature matching struggles);
 *   • CSP-safe — pure radix-2 Cooley–Tukey FFT, no eval / new Function, so it
 *     runs in a Worker and passes the site CSP (OpenCV.js does not).
 * Rotation/zoom (Fourier–Mellin) and a jsfeat homography fallback are layered on
 * top later for the minority of clips with significant rotation/parallax.
 *
 * Loads as `self.RtMotion` (worker / browser) AND `module.exports` (node tests).
 * No DOM dependency — operates on plain Float arrays of luma.
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.RtMotion = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    /* ───────────────────────── radix-2 FFT (1D, in place) ─────────────────────────
       Iterative Cooley–Tukey. re/im are Float64Array of length n = power of two.
       inverse=true also divides by n. */
    function fft1d(re, im, inverse) {
        var n = re.length;
        // bit-reversal permutation
        for (var i = 1, j = 0; i < n; i++) {
            var bit = n >> 1;
            for (; j & bit; bit >>= 1) j ^= bit;
            j ^= bit;
            if (i < j) {
                var tr = re[i]; re[i] = re[j]; re[j] = tr;
                var ti = im[i]; im[i] = im[j]; im[j] = ti;
            }
        }
        for (var len = 2; len <= n; len <<= 1) {
            var ang = (inverse ? 2 : -2) * Math.PI / len;
            var wr = Math.cos(ang), wi = Math.sin(ang);
            var half = len >> 1;
            for (var s = 0; s < n; s += len) {
                var cr = 1, ci = 0;
                for (var k = 0; k < half; k++) {
                    var a = s + k, b = a + half;
                    var xr = re[b] * cr - im[b] * ci;
                    var xi = re[b] * ci + im[b] * cr;
                    re[b] = re[a] - xr; im[b] = im[a] - xi;
                    re[a] += xr;        im[a] += xi;
                    var ncr = cr * wr - ci * wi;
                    ci = cr * wi + ci * wr; cr = ncr;
                }
            }
        }
        if (inverse) { for (var t = 0; t < n; t++) { re[t] /= n; im[t] /= n; } }
    }

    /* 2D FFT via row–column decomposition. re/im sized w*h, w & h powers of two. */
    function fft2d(re, im, w, h, inverse) {
        var rr = new Float64Array(w), ri = new Float64Array(w);
        for (var y = 0; y < h; y++) {
            var off = y * w;
            for (var x = 0; x < w; x++) { rr[x] = re[off + x]; ri[x] = im[off + x]; }
            fft1d(rr, ri, inverse);
            for (x = 0; x < w; x++) { re[off + x] = rr[x]; im[off + x] = ri[x]; }
        }
        var cr = new Float64Array(h), ci = new Float64Array(h);
        for (var cx = 0; cx < w; cx++) {
            for (var cy = 0; cy < h; cy++) { var k = cy * w + cx; cr[cy] = re[k]; ci[cy] = im[k]; }
            fft1d(cr, ci, inverse);
            for (cy = 0; cy < h; cy++) { var k2 = cy * w + cx; re[k2] = cr[cy]; im[k2] = ci[cy]; }
        }
    }

    /* ───────────────────────── windowing ─────────────────────────
       Separable Hann window suppresses the spectral leakage from the image
       borders (the frame is not periodic), which otherwise plants a false
       correlation peak at (0,0). */
    function hann(i, n) { return 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / (n - 1)); }

    /* parabolic sub-pixel offset from three samples around a peak */
    function parabola(l, c, r) {
        var denom = (l - 2 * c + r);
        if (Math.abs(denom) < 1e-12) return 0;
        var d = 0.5 * (l - r) / denom;
        return (d > 1 || d < -1) ? 0 : d;
    }

    /**
     * phaseCorrelate(a, b, w, h, weight?) → { dx, dy, peak, response }
     *
     * a, b: Float arrays length w*h (luma, any scale). w,h power of two.
     * weight: optional Float array length w*h in [0,1] — multiply BOTH frames by
     *         it before the transform to down-weight the overlay region (so the
     *         static text/logo does not bias the peak toward zero motion).
     *
     * Returns the translation (dx, dy) of frame B relative to frame A: a feature
     * at position p in A appears at p + (dx, dy) in B. `peak` is the raw
     * correlation height; `response` is peak / second-peak ratio (confidence;
     * ~1 = flat/ambiguous, large = sharp/reliable).
     */
    /* separable Hann × optional weight window — precomputed once, reused. */
    function buildWindow(w, h, weight) {
        var win = new Float64Array(w * h);
        var wy = new Float64Array(h), wx = new Float64Array(w);
        for (var y = 0; y < h; y++) wy[y] = hann(y, h);
        for (var x = 0; x < w; x++) wx[x] = hann(x, w);
        for (var yy = 0; yy < h; yy++) {
            var off = yy * w, hy = wy[yy];
            for (var xx = 0; xx < w; xx++) { var idx = off + xx; win[idx] = hy * wx[xx] * (weight ? weight[idx] : 1); }
        }
        return win;
    }

    /* windowed forward FFT of a field → {re, im}. Cache this per frame and reuse
       it across every correlation in the temporal window (the big speed win). */
    function prepareFFT(field, w, h, win) {
        var n = w * h, re = new Float64Array(n), im = new Float64Array(n);
        for (var i = 0; i < n; i++) re[i] = field[i] * win[i];
        fft2d(re, im, w, h, false);
        return { re: re, im: im };
    }

    /* normalized cross-power-spectrum correlation of two prepared FFTs. */
    function correlatePrepared(A, B, w, h) {
        var n = w * h, ar = A.re, ai = A.im, br = B.re, bi = B.im;
        var rr = new Float64Array(n), ri = new Float64Array(n);
        for (var i = 0; i < n; i++) {
            var pr = ar[i] * br[i] + ai[i] * bi[i];   // A · conj(B)
            var pi = ai[i] * br[i] - ar[i] * bi[i];
            var mag = Math.sqrt(pr * pr + pi * pi) + 1e-12;
            rr[i] = pr / mag; ri[i] = pi / mag;
        }
        fft2d(rr, ri, w, h, true); // inverse → correlation surface in rr

        var peak = -Infinity, px = 0, py = 0;
        for (i = 0; i < n; i++) { if (rr[i] > peak) { peak = rr[i]; px = i % w; py = (i / w) | 0; } }
        var second = -Infinity;
        for (i = 0; i < n; i++) {
            var sx = i % w, sy = (i / w) | 0;
            var ddx = Math.min(Math.abs(sx - px), w - Math.abs(sx - px));
            var ddy = Math.min(Math.abs(sy - py), h - Math.abs(sy - py));
            if (ddx + ddy < 3) continue;
            if (rr[i] > second) second = rr[i];
        }
        var at = function (xx, yy) { xx = ((xx % w) + w) % w; yy = ((yy % h) + h) % h; return rr[yy * w + xx]; };
        var subx = parabola(at(px - 1, py), peak, at(px + 1, py));
        var suby = parabola(at(px, py - 1), peak, at(px, py + 1));
        var dx = px + subx, dy = py + suby;
        if (dx > w / 2) dx -= w;
        if (dy > h / 2) dy -= h;
        // peak sits at −shift (A·conj(B) IFFT); negate to report B's shift vs A.
        return { dx: -dx, dy: -dy, peak: peak, response: peak / (Math.abs(second) + 1e-9) };
    }

    function phaseCorrelate(a, b, w, h, weight) {
        var win = buildWindow(w, h, weight);
        return correlatePrepared(prepareFFT(a, w, h, win), prepareFFT(b, w, h, win), w, h);
    }

    /* ───────────────────────── affine (zoom / rotation / pan) ─────────────────────────
       A single global translation can't model zoom, rotation or perspective drift.
       We tile the frame into a grid of blocks, phase-correlate each (skipping blocks
       that overlap the overlay or whose peak is ambiguous), then RANSAC-fit a global
       6-DOF affine to the block-centre→matched-point correspondences. Reuses the same
       proven FFT phase correlation — no extra library, CSP-clean. */

    /* solve 3×3 linear system A x = b (Gaussian elimination, partial pivot). */
    function solve3(A, b) {
        var m = [[A[0][0], A[0][1], A[0][2], b[0]],
                 [A[1][0], A[1][1], A[1][2], b[1]],
                 [A[2][0], A[2][1], A[2][2], b[2]]];
        for (var c = 0; c < 3; c++) {
            var piv = c;
            for (var r = c + 1; r < 3; r++) if (Math.abs(m[r][c]) > Math.abs(m[piv][c])) piv = r;
            if (Math.abs(m[piv][c]) < 1e-9) return null;
            var t = m[c]; m[c] = m[piv]; m[piv] = t;
            for (var r2 = 0; r2 < 3; r2++) {
                if (r2 === c) continue;
                var f = m[r2][c] / m[c][c];
                for (var k = c; k < 4; k++) m[r2][k] -= f * m[c][k];
            }
        }
        return [m[0][3] / m[0][0], m[1][3] / m[1][1], m[2][3] / m[2][2]];
    }

    /* exact affine from 3 correspondences (x,y)->(X,Y). Returns [a,b,c,d,e,f]. */
    function affineFrom3(p0, p1, p2) {
        var M = [[p0.x, p0.y, 1], [p1.x, p1.y, 1], [p2.x, p2.y, 1]];
        var abc = solve3(M, [p0.X, p1.X, p2.X]);
        var def = solve3(M, [p0.Y, p1.Y, p2.Y]);
        if (!abc || !def) return null;
        return [abc[0], abc[1], abc[2], def[0], def[1], def[2]];
    }

    /* least-squares affine from N correspondences (normal equations). */
    function affineLS(pts) {
        var sxx = 0, sxy = 0, sx = 0, syy = 0, sy = 0, n = pts.length;
        var sXx = 0, sXy = 0, sX = 0, sYx = 0, sYy = 0, sY = 0;
        for (var i = 0; i < n; i++) {
            var p = pts[i];
            sxx += p.x * p.x; sxy += p.x * p.y; sx += p.x; syy += p.y * p.y; sy += p.y;
            sXx += p.X * p.x; sXy += p.X * p.y; sX += p.X;
            sYx += p.Y * p.x; sYy += p.Y * p.y; sY += p.Y;
        }
        var ATA = [[sxx, sxy, sx], [sxy, syy, sy], [sx, sy, n]];
        var abc = solve3(ATA, [sXx, sXy, sX]);
        var def = solve3(ATA, [sYx, sYy, sY]);
        if (!abc || !def) return null;
        return [abc[0], abc[1], abc[2], def[0], def[1], def[2]];
    }

    function applyAffine(a, x, y) { return [a[0] * x + a[1] * y + a[2], a[3] * x + a[4] * y + a[5]]; }

    /* opts.rand — необязательный источник случайности (функция → [0,1)). Без
       него — Math.random, как всегда. Сеяный источник даёт ТОТ ЖЕ ответ на тот
       же кадр при каждом прогоне и в любом воркере: /video-stabilizer сеет по
       номеру кадра, и один файл больше не даёт разный завал от раза к разу
       (замер 30.09: угол пары расходился на 0.03° между прогонами). */
    function ransacAffine(corr, opts) {
        var iters = opts.iters || 300, thr = opts.thresh || 1.5, thr2 = thr * thr;
        var rnd = typeof opts.rand === 'function' ? opts.rand : Math.random;
        var best = null, bestIn = -1, n = corr.length;
        for (var it = 0; it < iters; it++) {
            var i0 = (rnd() * n) | 0, i1, i2;
            do { i1 = (rnd() * n) | 0; } while (i1 === i0);
            do { i2 = (rnd() * n) | 0; } while (i2 === i0 || i2 === i1);
            var aff = affineFrom3(corr[i0], corr[i1], corr[i2]);
            if (!aff) continue;
            var inl = 0;
            for (var k = 0; k < n; k++) {
                var c = corr[k], px = aff[0] * c.x + aff[1] * c.y + aff[2], py = aff[3] * c.x + aff[4] * c.y + aff[5];
                var dx = px - c.X, dy = py - c.Y;
                if (dx * dx + dy * dy < thr2) inl++;
            }
            if (inl > bestIn) { bestIn = inl; best = aff; }
        }
        if (!best) return null;
        var inliers = [];
        for (var k2 = 0; k2 < n; k2++) {
            var c2 = corr[k2], px2 = best[0] * c2.x + best[1] * c2.y + best[2], py2 = best[3] * c2.x + best[4] * c2.y + best[5];
            var dx2 = px2 - c2.X, dy2 = py2 - c2.Y;
            if (dx2 * dx2 + dy2 * dy2 < thr2) inliers.push(c2);
        }
        var refit = (inliers.length >= 3 && affineLS(inliers)) || best;
        return { affine: refit, inliers: inliers.length, blocks: n };
    }

    /**
     * estimateAffine(A, B, w, h, weight?, opts?) → { affine:[a,b,c,d,e,f], inliers, blocks } | null
     * affine maps a point in frame A to its location in frame B. weight (0..1, w*h)
     * down-weights/excludes the overlay. Returns null if too small or too few good
     * blocks (caller falls back to global translation phaseCorrelate).
     */
    /* Precompute per-block windowed FFTs for ONE frame. Cache this per frame in the
       pipeline ring → each frame's block FFTs are computed once and reused for every
       correlation in the temporal window (≈ window-size speed-up vs recomputing).
       The window folds in the overlay weight; since the overlay position is constant
       across frames, the kept-block set is identical frame-to-frame → blocks align. */
    function prepareFrameBlocks(luma, w, h, weight, opts) {
        opts = opts || {};
        var BS = opts.block || 64, step = opts.step || BS, half = BS / 2, area = BS * BS;
        var blocks = [];
        if (w < BS || h < BS) return { BS: BS, blocks: blocks };
        var blk = new Float64Array(BS * BS);
        for (var cy0 = 0; cy0 + BS <= h; cy0 += step) {
            for (var cx0 = 0; cx0 + BS <= w; cx0 += step) {
                var wsum = 0, win = new Float64Array(BS * BS);
                for (var yy = 0; yy < BS; yy++) {
                    var srow = (cy0 + yy) * w + cx0, drow = yy * BS, hy = hann(yy, BS);
                    for (var xx = 0; xx < BS; xx++) {
                        var si = srow + xx, di = drow + xx;
                        blk[di] = luma[si];
                        var ww = weight ? weight[si] : 1; wsum += ww;
                        win[di] = hy * hann(xx, BS) * ww;
                    }
                }
                if (wsum < 0.5 * area) continue;     // block too covered by overlay
                blocks.push({ cx: cx0 + half, cy: cy0 + half, fft: prepareFFT(blk, BS, BS, win) });
            }
        }
        return { BS: BS, blocks: blocks };
    }

    /* affine from two PREPARED frames (cached block FFTs). */
    function estimateAffineFromPrepared(pa, pb, opts) {
        opts = opts || {};
        var BS = pa.BS, minResp = opts.minResponse || 1.3, corr = [];
        var n = Math.min(pa.blocks.length, pb.blocks.length);
        for (var i = 0; i < n; i++) {
            var a = pa.blocks[i], b = pb.blocks[i];
            if (a.cx !== b.cx || a.cy !== b.cy) continue;   // mask differs → skip misaligned block
            var r = correlatePrepared(a.fft, b.fft, BS, BS);
            if (r.response < minResp) continue;
            corr.push({ x: a.cx, y: a.cy, X: a.cx + r.dx, Y: a.cy + r.dy });
        }
        if (corr.length < 6) return null;
        return ransacAffine(corr, opts);
    }

    function estimateAffine(A, B, w, h, weight, opts) {
        opts = opts || {};
        var pa = prepareFrameBlocks(A, w, h, weight, opts);
        if (pa.blocks.length < 6) return null;
        var pb = prepareFrameBlocks(B, w, h, weight, opts);
        return estimateAffineFromPrepared(pa, pb, opts);
    }

    /* ───────────────────────── gross translation (alias-free) ─────────────────────────
       Block phase correlation aliases when a block's motion exceeds half the block
       (±32 px for 64-blocks) — fatal for far-frame candidates (subtitles, fast pans).
       A FULL-frame phase correlation has no aliasing up to ±half the frame, so we use
       it for the gross translation and reserve block-affine for SMALL-motion zoom/
       rotation. Confirmed empirically: block-affine wraps past k≈6 while the full-frame
       estimate stays exact. */
    function pow2le(v, cap) { var p = 16; while (p * 2 <= v && p * 2 <= cap) p *= 2; return p; }

    function resampleLuma(src, sw, sh, dw, dh) {
        if (sw === dw && sh === dh) return src;
        var out = new Float64Array(dw * dh), rx = sw / dw, ry = sh / dh;
        for (var y = 0; y < dh; y++) {
            var fy = (y + 0.5) * ry - 0.5; if (fy < 0) fy = 0; else if (fy > sh - 1) fy = sh - 1;
            var y0 = fy | 0, y1 = y0 + 1 < sh ? y0 + 1 : y0, wy = fy - y0;
            for (var x = 0; x < dw; x++) {
                var fx = (x + 0.5) * rx - 0.5; if (fx < 0) fx = 0; else if (fx > sw - 1) fx = sw - 1;
                var x0 = fx | 0, x1 = x0 + 1 < sw ? x0 + 1 : x0, wx = fx - x0;
                var a = src[y0 * sw + x0], b = src[y0 * sw + x1], c = src[y1 * sw + x0], d = src[y1 * sw + x1];
                out[y * dw + x] = a * (1 - wx) * (1 - wy) + b * wx * (1 - wy) + c * (1 - wx) * wy + d * wx * wy;
            }
        }
        return out;
    }

    /* full-frame windowed FFT (resampled to pow2). Cache per frame; reuse across window. */
    function prepareFrameFull(luma, w, h, weight, opts) {
        opts = opts || {};
        var cap = opts.cap || 512;
        var fw = pow2le(w, cap), fh = pow2le(h, cap);
        var rl = resampleLuma(luma, w, h, fw, fh);
        var rwt = weight ? resampleLuma(weight, w, h, fw, fh) : null;
        var fft = prepareFFT(rl, fw, fh, buildWindow(fw, fh, rwt));
        return { re: fft.re, im: fft.im, fw: fw, fh: fh, sx: w / fw, sy: h / fh };
    }

    /* gross translation between two prepared full-frame FFTs, in original coords. */
    function globalTranslation(A, B) {
        var r = correlatePrepared({ re: A.re, im: A.im }, { re: B.re, im: B.im }, A.fw, A.fh);
        return { x: r.dx * A.sx, y: r.dy * A.sy, response: r.response };
    }

    /* HYBRID warp (target→neighbour): gross translation (full-frame, alias-free) +
       block-affine refinement only when the motion is small enough for blocks to be
       accurate (captures zoom/rotation). Returns [a,b,c,d,e,f]. */
    function hybridWarp(fullA, fullB, prepA, prepB, opts) {
        opts = opts || {};
        var thr = opts.blockThreshold || 24;
        var g = globalTranslation(fullA, fullB);
        if (Math.hypot(g.x, g.y) >= thr) return [1, 0, g.x, 0, 1, g.y];
        if (prepA && prepB) {
            var a = estimateAffineFromPrepared(prepA, prepB, opts);
            if (a && a.inliers >= (opts.minInliers || 6)) return a.affine;
        }
        return [1, 0, g.x, 0, 1, g.y];
    }

    return {
        fft1d: fft1d,
        fft2d: fft2d,
        hann: hann,
        buildWindow: buildWindow,
        prepareFFT: prepareFFT,
        correlatePrepared: correlatePrepared,
        phaseCorrelate: phaseCorrelate,
        prepareFrameBlocks: prepareFrameBlocks,
        estimateAffineFromPrepared: estimateAffineFromPrepared,
        estimateAffine: estimateAffine,
        prepareFrameFull: prepareFrameFull,
        globalTranslation: globalTranslation,
        hybridWarp: hybridWarp,
        resampleLuma: resampleLuma,
        affineFrom3: affineFrom3,
        affineLS: affineLS,
        applyAffine: applyAffine
    };
});
